import {_} from 'lodash';
import * as ko from "knockout";
import {IdName, IMessageData, IObjectType, IRouteDefinitionParams, ISyncField} from '../../../index';
import {global, lm} from "../../../appContext";
import PromiseRequest from "../../../req/PromiseRequest";
import {
    createErrorMessage,
    createErrorString,
    getObjectDefinition,
    IObjectDefinition,
    isPartOf
} from "../../../util/misc";
import {REQUEST_MESSAGE, NO_DATA_MESSAGE} from "../../../constants/Language";
import {storeObservable, storeObservableArray} from "../../../util/sessionstore";
import {ImportFormatId} from "../../../constants/ImportFormats";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {MAX_CONCURRENT_REQUESTS, ObjectTypeId, ObjectTypeId2ImportFormatId} from "../../../constants";
import {nsRoute} from "../../../util/route";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {ErrorObject} from "../../../error/ErrorObject";
import {ErrorManager} from "../../../error/ErrorManager";
import {ButtonManager} from "../../../util/ButtonManager";
import {
    getBackButton,
    getDiscardButton,
    getNextButton,
    getPreviousButton,
    getSubmitButton
} from "../../../constants/RouteDefinitions";
import {nsLanguage} from "../../../util/nsLanguage";
import {TServiceName} from "../../../constants/ApiServices";
import {IComboboxV2Options} from "../../combobox/v2";
import {createBaseTextMatch} from "../../combobox/v2-sources";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import ButtonOrder = nsRoute.ButtonOrder;
import allHtml from './all.html?raw';

export let template = allHtml;

enum Step {
    Init = 'init',
    Form = 'form',
    Load = 'load',
    List = 'list',
    Process = 'process',
    Finish = 'finish',
    Error = 'error',
}

interface IContext {
    importformat: KnockoutObservable<IdName | null>
    language: KnockoutObservable<IdName | null>
    err_importformat: ErrorObject
    err_language: ErrorObject
}

interface IResult {
    syncfieldItem: SyncfieldItem
    syncfield: ISyncField
    reason?: string
}

export class viewModel extends ParsedRouteDataDecorator {
    private Step = Step

    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null)
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly objecttype: KnockoutObservable<IObjectType | null> = ko.observable(null)
    private readonly err_objecttype: ErrorObject = new ErrorObject()

    private readonly context: IContext = {
        importformat: ko.observable(null),
        language: ko.observable(null),
        err_importformat: new ErrorObject(),
        err_language: new ErrorObject(),
    }

    private readonly context_target: IContext = {
        importformat: ko.observable(null),
        language: ko.observable(null),
        err_importformat: new ErrorObject(),
        err_language: new ErrorObject(),
    }

    private rawSrc!: ISyncField[]
    private rawTrg!: ISyncField[]
    private dataSrc!: ISyncField[]
    private dataTrg!: ISyncField[]
    private data: KnockoutObservableArray<SyncfieldItem> = ko.observableArray<SyncfieldItem>([])

    private requests!: PromiseRequest[]
    private successes: KnockoutObservableArray<IResult> = ko.observableArray<IResult>([])
    private errors: KnockoutObservableArray<IResult> = ko.observableArray<IResult>([])

    private allObjectTypes!: IObjectType[];
    private allLanguages!: IdName[];
    private allImportFormats!: IdName[];

    private kosub = new KOSubscriptionTracker()

    private step: KnockoutObservable<Step | null> = ko.observable(null)

    private em: ErrorManager = new ErrorManager()

    private pageButtons = new ButtonManager()

    private readonly list = {
        data: this.data,
        resultsprpage: storeObservable('num.search.results'),
        pagenr: ko.observable(0),
        pages: ko.pureComputed((): number => Math.ceil(this.data().length / storeObservable('num.search.results')())),
    }

    constructor(params: IRouteDefinitionParams) {
        super(params)

        this.kosub.subscribe(this.step, (step: Step | null) => {
            if (step) {
                this.onStepChangeHandler(step)
            }
        })
        this.kosub.subscribeChanged(this.list.resultsprpage, (newValue, oldValue) => {
            if (newValue > oldValue) {
                this.list.pagenr(0)
            }
        })

        this.err_objecttype.addValidator({
            fn: () => !!this.objecttype(),
            message: `invalid value`,
        })

        this.context.err_importformat.addValidator({
            fn: () => !!this.context.importformat(),
            message: `invalid value`,
        })

        this.context.err_language.addValidator({
            fn: () => !!this.context.language(),
            message: `invalid value`,
        })

        this.context_target.err_importformat.addValidator({
            fn: () => !!this.context_target.importformat(),
            message: `invalid value`,
        })

        this.context_target.err_language.addValidator({
            fn: () => !!this.context_target.language(),
            message: `invalid value`,
        })

        const sameImportFormatAndLanguage = (): boolean => {
            if (!this.isStep(Step.Form)) {
                return false
            }
            const i1 = this.context.importformat()?.id
            const i2 = this.context_target.importformat()?.id
            const l1 = this.context.language()?.id
            const l2 = this.context_target.language()?.id
            const equalImportformat = this.enableImportFormat() ? i1 == i2 : true
            const equalLanguage = l1 == l2
            return equalImportformat && equalLanguage
        }

        this.context_target.err_language.addValidator({
            fn: () => !sameImportFormatAndLanguage(),
            message: `same language for source and target`,
        })

        this.context_target.err_importformat.addValidator({
            fn: () => !sameImportFormatAndLanguage(),
            message: `same import format for source and target`,
        })

        this.em.addErrorObject(this.err_objecttype)
        this.em.addErrorObject(this.context.err_importformat)
        this.em.addErrorObject(this.context.err_language)
        this.em.addErrorObject(this.context_target.err_importformat)
        this.em.addErrorObject(this.context_target.err_language)

        this.pageButtons.add(_.defaultsDeep(getBackButton(), {
            enabled: () => !this.isStep(Step.Process),
        }))

        this.pageButtons.add(getPreviousButton({
            onClick: this.onPreviousClick.bind(this),
            order: ButtonOrder.PREVIOUS,
            enabled: this.isPreviousValid.bind(this),
            validator: () => this.isStep(Step.Form, Step.List),
        }))

        this.pageButtons.add(getNextButton({
            onClick: this.onNextClick.bind(this),
            order: ButtonOrder.NEXT,
            enabled: this.isNextValid.bind(this),
            validator: () => this.isStep(Step.Form),
        }))

        this.pageButtons.add(getSubmitButton({
            onClick: this.onNextClick.bind(this),
            order: ButtonOrder.SUBMIT,
            enabled: this.isNextValid.bind(this),
            validator: () => this.isStep(Step.List),
            label: () => {
                const num = this.num_checked_all()
                if (!num) {
                    return '((copy))'
                }
                const numLocalString = nsLanguage.toLocaleString(num)
                return `((copy)) (${numLocalString} ((systemfield|${numLocalString}|ac)))`
            },
            icon: 'fa-copy',
        }))

        this.pageButtons.add(getDiscardButton({
            label: 'start over',
            onClick: () => this.step(Step.Init),
            validator: () => this.isStep(Step.Finish, Step.Error),
        }))

        this.step(Step.Init)
    }

    private onStepChangeHandler(step: Step) {
        switch (step) {

            /*** Step Init ********************************************************************************************/
            case Step.Init: {
                this.messageData({message: REQUEST_MESSAGE})
                Promise.resolve()
                    .then(() => global.getPromiseRequest('getobjecttypes').request()
                        .then((data: IObjectType[]) => {
                            this.allObjectTypes = data
                            const storedId = storeObservable('syncfields.config.objecttypeid')()
                            if (storedId) {
                                this.objecttype(this.allObjectTypes.find(o => o.id == storedId) ?? null)
                            }
                        })
                    )
                    .then(() => global.getPromiseRequest('getlanguages').request()
                        .then((data: IdName[]) => {
                            this.allLanguages = data
                            const storedId = storeObservableArray('syncfields.config.language')()?.[0]
                            let language: IdName | null = null
                            if (storedId) {
                                language = this.allLanguages.find(o => o.id == storedId) ?? null
                            }
                            this.context.language(language)
                            this.context_target.language(language)
                        })
                    )
                    .then(() => global.getPromiseRequest('getimportformats').request()
                        .then((data: IdName[]) => {
                            this.allImportFormats = data
                            const storedId = storeObservable('syncfields.config.importformat')()
                            if (storedId) {
                                const importformat = this.allImportFormats.find(o => o.id == storedId);
                                this.context.importformat(importformat ?? null)
                                this.context_target.importformat(importformat ?? null)
                            }
                        })
                    )
                    .then(() => {
                        this.messageData(null)
                        this.step(Step.Form)
                    })
                    .catch((reason: string) => {
                        this.messageData(createErrorMessage(String(reason)))
                        this.step(Step.Error)
                    })
                break
            }
            /*** Step Form ********************************************************************************************/
            case Step.Form: {
                break
            }
            /*** Step Load ********************************************************************************************/
            case Step.Load: {
                this.messageData({message: REQUEST_MESSAGE})
                const objectType = this.objecttype();
                const srcImportFormat = this.context.importformat();
                const srcLanguage = this.context.language();
                const trgLanguage = this.context_target.language();
                const trgImportFormat = this.context_target.importformat();
                if (!objectType || !srcImportFormat || !srcLanguage || !trgLanguage || !trgImportFormat) {
                    this.messageData(createErrorMessage('invalid value'))
                    this.step(Step.Error)
                    break
                }
                const src_objectTypeId = objectType.id;
                const src_languageId = srcLanguage.id;
                const src_importFormatId = srcImportFormat.id;
                const trg_languageId = trgLanguage.id;
                const trg_importFormatId = trgImportFormat.id;

                Promise.resolve()
                    .then(() => nsSyncFields
                        .getSyncFields(src_objectTypeId, src_languageId, src_importFormatId)
                        .request
                        .then((data: ISyncField[]) => this.rawSrc = data)
                    )
                    .then(() => nsSyncFields
                        .getSyncFields(src_objectTypeId, trg_languageId, trg_importFormatId)
                        .request
                        .then((data: ISyncField[]) => this.rawTrg = data)
                    )
                    .then(() => {
                        this.dataSrc = this._processArr(this.rawSrc)
                        this.dataTrg = this._processArr(this.rawTrg)

                        const data: SyncfieldItem[] = []
                        this.dataSrc.forEach(srcObj => {
                            const trgObj = this.rawTrg.find(trgObj => trgObj.syncfieldid == srcObj.syncfieldid)
                            const item = new SyncfieldItem(srcObj, trgObj)
                            data.push(item)
                        })

                        this.data(data)
                        this.selectMissing()

                        this.messageData(null)
                        this.step(Step.List)
                    })
                    .catch((reason: string) => {
                        this.messageData(createErrorMessage(String(reason)))
                        this.step(Step.Error)
                    })
                break
            }

            /*** Step List ********************************************************************************************/
            case Step.List: {
                break
            }

            /*** Step Process ********************************************************************************************/
            case Step.Process: {
                this.requests?.forEach(p => p.abort())
                this.requests = []
                this.successes([])
                this.errors([])

                const list = this.getChecked()

                const doNext = (): Promise<any> => {
                    if (!list.length) {
                        return Promise.resolve()
                    }
                    const syncfieldItem = list.shift()
                    if (!syncfieldItem) {
                        return Promise.resolve()
                    }
                    const syncfield: ISyncField = _.defaultsDeep({}, syncfieldItem.src, syncfieldItem.trg ?? {})
                    syncfield.importformatid = this.context_target.importformat()?.id ?? syncfield.importformatid
                    syncfield.languageid = this.context_target.language()?.id ?? syncfield.languageid
                    let serviceName: TServiceName;
                    if (!!syncfieldItem.trg?.syncfielddisplaynameid) {
                        serviceName = 'updatesyncfielddisplayname';
                        syncfield.syncfielddisplaynameid = syncfieldItem.trg.syncfielddisplaynameid
                    } else {
                        serviceName = 'createsyncfielddisplayname';
                        syncfield.syncfielddisplaynameid = 0
                    }

                    const promiseRequest = new PromiseRequest(serviceName)
                    this.requests.push(promiseRequest)
                    return promiseRequest
                        .request({jdisplayname: JSON.stringify(syncfield)})
                        .then(() => {
                            const result: IResult = {syncfield, syncfieldItem}
                            this.successes.push(result)
                            return doNext()
                        })
                        .catch((reason: string) => {
                            const result: IResult = {syncfield, syncfieldItem, reason}
                            this.errors.push(result)
                            return doNext()
                        })
                }

                const all: Promise<any>[] = []
                const num = list.length
                for (let i = 0; i < Math.min(num, MAX_CONCURRENT_REQUESTS); i++) {
                    all.push(doNext())
                }
                Promise.all(all)
                    .then(() => {
                        global.cacheManager.removeCache('getsyncfields')
                        this.step(Step.Finish)
                    })
                    .catch((reason: string) => {
                        global.cacheManager.removeCache('getsyncfields')
                        this.step(Step.Error)
                        this.messageData(createErrorMessage(String(reason)))
                    })
                break
            }

            /*** Step Finish ******************************************************************************************/
            case Step.Finish: {
                break
            }

            /*** Step Error *******************************************************************************************/
            case Step.Error: {
                break
            }

        }
    }

    private _processArr(data: ISyncField[]): ISyncField[] {
        return _(data)
            .filter((o: ISyncField) => !!o.syncfielddisplaynameid)
            .sortBy((o: ISyncField) => o.name)
            .value()
    }

    private objDef = ko.pureComputed((): IObjectDefinition | null => {
        const objectType = this.objecttype();
        if (!objectType) {
            return null
        }
        return getObjectDefinition(objectType)
    })

    private readonly enableImportFormat: KnockoutComputed<boolean> = ko.pureComputed((): boolean => {
        // unless there is a default import format it should be mutable
        return !this.getDefaultImportFormatId(this.objecttype()?.id ?? null)
    })

    private readonly getRealImportFormat = ko.pureComputed((): IdName | null => {
        const objectTypeId = this.objecttype()?.id;
        if (!objectTypeId) {
            return null
        }
        const defaultImportFormatId = this.getDefaultImportFormatId(objectTypeId)
        if (defaultImportFormatId) {
            return this.allImportFormats.find(o => o.id == defaultImportFormatId) ?? null
        }
        return this.context.importformat()
    })

    private getDefaultImportFormatId(objectTypeId: ObjectTypeId | null | undefined): ImportFormatId | null {
        if (!objectTypeId) {
            return null
        }
        return ObjectTypeId2ImportFormatId[objectTypeId] ?? null
    }

    /** Language combobox for copy source/target (legacy base options adapted to v2). */
    private getLanguageComboboxOptions(selected: KnockoutObservable<IdName | null>): IComboboxV2Options<IdName> {
        const labelFn = (o: IdName) => o.name
        return {
            data: this.allLanguages,
            selected,
            emptyPreparedListMessage: NO_DATA_MESSAGE,
            inputAttrs: {placeholder: lm.t('choose|td') ?? ''},
            option: {
                label: labelFn,
                match: createBaseTextMatch(labelFn),
            },
        }
    }

    private selectMissing() {
        this.data().forEach(o => o.selectMissing())
        global.snackbarData({message: `${this.num_checked_all()} systemfelter er valgt.`})
    }

    /*******************************************************************************************************************
     * NAVIGATION
     ******************************************************************************************************************/

    private isStep(...steps: Step[]):
        boolean {
        const currentStep = this.step();
        return !!currentStep && isPartOf<Step>(currentStep, ...steps)
    }

    private onPreviousClick() {
        switch (this.step()) {
            case Step.List:
                this.step(Step.Form)
                break
        }
    }

    private onNextClick() {
        switch (this.step()) {
            case Step.Form:
                this.step(Step.Load)
                break
            case Step.List:
                this.step(Step.Process)
                break
        }
    }

    private isPreviousValid = ko.pureComputed((): boolean => {
        switch (this.step()) {
            case Step.List:
                return true
            default:
                return false
        }
    })

    private isNextValid = ko.pureComputed((): boolean => {
        switch (this.step()) {
            case Step.Form:
                return this.em.validate()
            case Step.List:
                return !!this.data().find(o => o.selected())
            default:
                return false
        }
    })


    /*******************************************************************************************************************
     * View helpers
     *******************************************************************************************************************/

    private getTitle = nsSyncFields.getTitle

    private readonly percentComplete = ko.pureComputed((): number => {
        const total = this.successes().length + this.errors().length
        return total / this.num_checked_all()
    })

    private readonly createErrorString = createErrorString

    /*******************************************************************************************************************
     * PAGE CHECKED
     ******************************************************************************************************************/

    private getPageList(): SyncfieldItem[] {
        const start = this.list.pagenr() * this.list.resultsprpage()
        const end = start + this.list.resultsprpage()
        return this.data().slice(start, end)
    }

    private readonly num_checked_page = ko.pureComputed((): number => {
        return this.getPageList().filter(o => o.selected()).length
    })

    private readonly some_checked_page = ko.pureComputed((): boolean => {
        const num = this.num_checked_page();
        return num > 0 && num < this.getPageList().length
    })

    private readonly checked_page = ko.pureComputed((): boolean => {
        return this.num_checked_page() == this.getPageList().length
    })

    private do_toggle_page() {
        const bool = this.num_checked_page() == this.getPageList().length
        this.getPageList().forEach(o => o.selected(!bool))
        return true
    }

    /*******************************************************************************************************************
     * ALL CHECKED
     ******************************************************************************************************************/

    private readonly getChecked = ko.pureComputed((): SyncfieldItem[] => {
        return this.data().filter(o => o.selected())
    })

    private readonly num_checked_all = ko.pureComputed((): number => {
        return this.getChecked().length
    })

    private readonly some_checked_all = ko.pureComputed((): boolean => {
        const num = this.num_checked_all();
        return num > 0 && num < this.data().length
    })

    private readonly checked_all = ko.pureComputed((): boolean => {
        return this.num_checked_all() == this.data().length
    })

    private do_toggle_all() {
        const bool = this.num_checked_all() == this.data().length
        this.data().forEach(o => o.selected(!bool))
        return true
    }

    /*******************************************************************************************************************
     * MISCELLANEOUS
     ******************************************************************************************************************/

    override dispose() {
        this.requests?.forEach(p => p.abort?.())
        this.kosub.unsuscribeAll()
    }
}

class SyncfieldItem {
    selected: KnockoutObservable<boolean> = ko.observable(false)

    constructor(public src: ISyncField, public trg?: ISyncField, selected: boolean = false) {
    }

    public selectMissing() {
        this.selected(!this.trg?.syncfielddisplaynameid)
    }
}